// StaticLayerEditor — aligns the persistent scene 3DGS against the playing
// sequence, and produces the value stored at metadata.staticLayer.transform.
//
// Design notes that matter:
//  * Only the SCENE moves. The performer's layer stays at identity: the AR
//    auto-fit box and the arOffset grounding are both measured about the
//    performer, so moving it would silently drift AR placement. Only the
//    difference between the two layers is observable anyway.
//  * Rotation is applied by incremental premultiply through the viewer handle,
//    never by decomposing to Euler angles and back. The repo already carries
//    three incompatible Euler conventions; a quaternion has none of that and
//    matches what RotationGizmo and the camera types already use.
//  * Scale is a single scalar. Spark collapses a decomposed non-uniform scale
//    to its mean, so (2,1,1) would render as a uniform 1.333 — authoring three
//    axes would be a lie.
//  * The A/B toggle detaches the scene mesh rather than setting visible=false.
//    A hidden SplatMesh left in the scene makes Spark mark the accumulator
//    dirty every frame and re-sort continuously.
import { useCallback, useEffect, useRef, useState } from 'react'
import { X, Save, RotateCcw, Eye, EyeOff } from 'lucide-react'
import { ModelViewer } from '../ModelViewer'
import type { ModelSource, LayerTransform } from '../ModelViewer/resolveModelSource'

/** Handle DualgsViewer exposes for live layer control. */
interface LayerViewerHandle {
  setLayerTransform?: (name: string, t: LayerTransform) => void
  getLayerTransform?: (name: string) => LayerTransform
  rotateLayer?: (name: string, axis: [number, number, number], angle: number) => void
  setStaticVisible?: (show: boolean) => void
  hasStaticLayer?: () => boolean
}

export const IDENTITY_TRANSFORM: LayerTransform = {
  position: { x: 0, y: 0, z: 0 },
  quaternion: { x: 0, y: 0, z: 0, w: 1 },
  scale: 1,
}

interface StaticLayerEditorProps {
  source: ModelSource
  currentTransform?: LayerTransform | null
  onSave: (transform: LayerTransform) => void
  onCancel: () => void
}

const POS_STEPS = [0.001, 0.01, 0.1] as const
const ROT_STEPS_DEG = [0.5, 5, 15] as const

export function StaticLayerEditor({
  source,
  currentTransform,
  onSave,
  onCancel,
}: StaticLayerEditorProps) {
  const viewerRef = useRef<LayerViewerHandle | null>(null)
  // The live scene graph is the source of truth while the modal is open; this
  // mirror only drives the number fields.
  const [transform, setTransform] = useState<LayerTransform>(
    () => currentTransform ?? IDENTITY_TRANSFORM,
  )
  const [posStep, setPosStep] = useState<number>(0.01)
  const [rotStepDeg, setRotStepDeg] = useState<number>(5)
  const [sceneVisible, setSceneVisible] = useState(true)
  const [ready, setReady] = useState(false)

  // Push the starting pose in once the viewer has mounted its layers.
  useEffect(() => {
    let cancelled = false
    const id = window.setInterval(() => {
      const v = viewerRef.current
      if (!v?.setLayerTransform || cancelled) return
      v.setLayerTransform('static', currentTransform ?? IDENTITY_TRANSFORM)
      setReady(true)
      window.clearInterval(id)
    }, 100)
    return () => { cancelled = true; window.clearInterval(id) }
  }, [currentTransform])

  /** Read the live layer back into the mirrored fields. */
  const syncFromViewer = useCallback(() => {
    const t = viewerRef.current?.getLayerTransform?.('static')
    if (t) setTransform(t)
  }, [])

  const nudgePosition = useCallback((axis: 'x' | 'y' | 'z', delta: number) => {
    const v = viewerRef.current
    if (!v?.getLayerTransform || !v.setLayerTransform) return
    const t = v.getLayerTransform('static')
    const next: LayerTransform = {
      ...t,
      position: { ...t.position, [axis]: t.position[axis] + delta },
    }
    v.setLayerTransform('static', next)
    setTransform(next)
  }, [])

  const setPosition = useCallback((axis: 'x' | 'y' | 'z', raw: string) => {
    const value = Number(raw)
    setTransform((prev) => {
      const next: LayerTransform = { ...prev, position: { ...prev.position, [axis]: raw === '' ? 0 : value } }
      if (Number.isFinite(value)) viewerRef.current?.setLayerTransform?.('static', next)
      return next
    })
  }, [])

  const rotate = useCallback((axis: [number, number, number], degrees: number) => {
    viewerRef.current?.rotateLayer?.('static', axis, (degrees * Math.PI) / 180)
    syncFromViewer()
  }, [syncFromViewer])

  const setScale = useCallback((raw: string) => {
    const value = Number(raw)
    setTransform((prev) => {
      const next: LayerTransform = { ...prev, scale: raw === '' ? 1 : value }
      if (Number.isFinite(value) && value > 0) viewerRef.current?.setLayerTransform?.('static', next)
      return next
    })
  }, [])

  const reset = useCallback(() => {
    viewerRef.current?.setLayerTransform?.('static', IDENTITY_TRANSFORM)
    setTransform(IDENTITY_TRANSFORM)
  }, [])

  const toggleScene = useCallback(() => {
    setSceneVisible((prev) => {
      viewerRef.current?.setStaticVisible?.(!prev)
      return !prev
    })
  }, [])

  const fmt = (n: number) => (Math.round(n * 1000) / 1000).toString()

  const stepPicker = <T extends number>(
    steps: readonly T[], value: number, onPick: (s: T) => void, suffix: string,
  ) => (
    <div className="flex gap-1">
      {steps.map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onPick(s)}
          className={`px-2 py-0.5 text-xs rounded border transition-colors ${
            value === s
              ? 'bg-indigo-600 border-indigo-500 text-white'
              : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-500'
          }`}
        >
          {s}{suffix}
        </button>
      ))}
    </div>
  )

  const axisRow = (axis: 'x' | 'y' | 'z') => (
    <div key={axis} className="flex items-center gap-2">
      <span className="w-4 text-sm text-gray-400 uppercase">{axis}</span>
      <button
        type="button"
        onClick={() => nudgePosition(axis, -posStep)}
        className="w-7 h-7 rounded bg-gray-800 border border-gray-700 text-gray-200 hover:border-gray-500"
      >−</button>
      <input
        type="number"
        step={posStep}
        value={fmt(transform.position[axis])}
        onChange={(e) => setPosition(axis, e.target.value)}
        className="w-24 px-2 py-1 text-sm rounded bg-gray-900 border border-gray-700 text-white"
      />
      <button
        type="button"
        onClick={() => nudgePosition(axis, posStep)}
        className="w-7 h-7 rounded bg-gray-800 border border-gray-700 text-gray-200 hover:border-gray-500"
      >+</button>
    </div>
  )

  const rotRow = (label: string, axis: [number, number, number]) => (
    <div key={label} className="flex items-center gap-2">
      <span className="w-4 text-sm text-gray-400">{label}</span>
      <button
        type="button"
        onClick={() => rotate(axis, -rotStepDeg)}
        className="w-7 h-7 rounded bg-gray-800 border border-gray-700 text-gray-200 hover:border-gray-500"
      >↺</button>
      <button
        type="button"
        onClick={() => rotate(axis, rotStepDeg)}
        className="w-7 h-7 rounded bg-gray-800 border border-gray-700 text-gray-200 hover:border-gray-500"
      >↻</button>
    </div>
  )

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-xl w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden border border-gray-800">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <div>
            <h3 className="text-lg font-semibold text-white">Scene Layer Alignment</h3>
            <p className="text-sm text-gray-400">
              Move the static scene into place. The performer stays fixed.
            </p>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-white" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 flex flex-col lg:flex-row min-h-0">
          <div className="flex-1 bg-black min-h-[320px] relative">
            <ModelViewer
              ref={viewerRef}
              source={source}
              className="w-full h-full"
              dualgs={{ showUi: false, showTimeline: true }}
            />
            {!source.staticUrl && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/70 text-center px-6">
                <p className="text-gray-300 text-sm">
                  This model has no scene layer yet. Upload one first, then align it here.
                </p>
              </div>
            )}
          </div>

          <div className="w-full lg:w-80 border-t lg:border-t-0 lg:border-l border-gray-800 p-5 space-y-5 overflow-y-auto">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-200">Position</span>
                {stepPicker(POS_STEPS, posStep, setPosStep, 'm')}
              </div>
              {(['x', 'y', 'z'] as const).map(axisRow)}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-200">Rotation</span>
                {stepPicker(ROT_STEPS_DEG, rotStepDeg, setRotStepDeg, '°')}
              </div>
              {rotRow('X', [1, 0, 0])}
              {rotRow('Y', [0, 1, 0])}
              {rotRow('Z', [0, 0, 1])}
              <p className="text-xs text-gray-500">
                Applied in the parent frame. Stored as a quaternion, so there are no gimbal poles.
              </p>
            </div>

            <div className="space-y-2">
              <span className="text-sm font-medium text-gray-200">Scale</span>
              <input
                type="number"
                step={0.01}
                min={0.001}
                value={fmt(transform.scale)}
                onChange={(e) => setScale(e.target.value)}
                className="w-full px-2 py-1 text-sm rounded bg-gray-900 border border-gray-700 text-white"
              />
              <p className="text-xs text-gray-500">
                Uniform only — Spark averages a non-uniform scale, so separate axes would not render as set.
              </p>
            </div>

            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={toggleScene}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm rounded bg-gray-800 border border-gray-700 text-gray-200 hover:border-gray-500"
              >
                {sceneVisible ? <EyeOff size={15} /> : <Eye size={15} />}
                {sceneVisible ? 'Hide scene' : 'Show scene'}
              </button>
              <button
                type="button"
                onClick={reset}
                title="Identity — correct when the scene was solved from the same COLMAP sparse/0"
                className="flex items-center justify-center gap-2 px-3 py-2 text-sm rounded bg-gray-800 border border-gray-700 text-gray-200 hover:border-gray-500"
              >
                <RotateCcw size={15} />
                Reset
              </button>
            </div>

            <p className="text-xs text-gray-500">
              {ready ? 'Live preview is driving the real viewer.' : 'Waiting for the viewer to load…'}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-800">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm rounded bg-gray-800 border border-gray-700 text-gray-200 hover:border-gray-500"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(viewerRef.current?.getLayerTransform?.('static') ?? transform)}
            className="flex items-center gap-2 px-4 py-2 text-sm rounded bg-indigo-600 text-white hover:bg-indigo-500"
          >
            <Save size={15} />
            Apply
          </button>
        </div>
      </div>
    </div>
  )
}

export default StaticLayerEditor
